import { Box, Flex, Heading, Skeleton, SkeletonText, Stack, Text, useBreakpointValue } from '@chakra-ui/react'
import { AnimatedUnderline } from '../../elements'
import { HighlightFirstWord } from '../../elements'
import dayjs from 'dayjs'
import { NextLink } from '@stocker/ui-components/design-system'
import { formatHeadlineColor, asComponent } from '@stocker/ui-components/helpers'
import type { IImage } from '@stocker/ui-components/design-system'
import NextImage from 'next/image'
import type React from 'react'
import { useIntl } from 'react-intl'

export interface INewsCardProps {
  displayDate: string
  image: IImage
  productNewsImage: IImage
  highlightedFirstWord?: string
  headline: string
  text: string
  link: {
    absolutePath: string
  }
  isLoaded?: boolean
}
interface IColors {
  colorText?: string
  colorBackground?: string
  colorAccent?: string
}
interface ICombined extends IColors, INewsCardProps {}

export const NewsCard: React.FC<ICombined> = ({
  image, productNewsImage, headline, highlightedFirstWord, text, link, displayDate, isLoaded = true,
  colorText = '', colorBackground = '', colorAccent = '',
}) => {
  const isMobile = useBreakpointValue({
    base: 'true',
    lg: '',
  }, '')
  const intl = useIntl()
  const img = productNewsImage && productNewsImage.fullpath !== "" ? productNewsImage : image

  return (
    <Stack
      direction={{ base: 'column', lg: 'row' }}
      w="100%"
      spacing={0}
      alignItems="center"
    >
      <Skeleton
        isLoaded={isLoaded}
        w={isLoaded ? '100%' : 'calc(100% - 30px)'}
        borderRadius="15px"
        px="15px"
      >
        <NextLink
          w="100%"
          href={`/news${link.absolutePath}`}
        >
          <NextImage
            src={img?.fullpath ?? '/'}
            width="446"
            height="268"
            placeholder={img?.blurDataUrl ? 'blur' : 'empty'}
            blurDataURL={img?.blurDataUrl ?? ''}
            sizes="100vw"
            style={{
              width: '100%',
              height: 'auto',
              objectFit: 'cover',
            }}
            alt={headline}
          />
        </NextLink>
      </Skeleton>
      <Flex
        w="100%"
        h="100%"
        color={colorText}
        alignItems="center"
        alignContent="center"
        px="15px"
      >
        <Box>
          <Skeleton isLoaded={isLoaded} w={isLoaded ? undefined : '100px'} h={isLoaded ? undefined : '20px'} mt={isLoaded ? undefined : { base: 3, md: '0' }}>
            <Text
              fontSize="small"
              pt={{ base: 3, md: '0' }}
            >{dayjs(displayDate).format('DD.MM.YYYY')}
            </Text>
          </Skeleton>
          <Skeleton isLoaded={isLoaded} mt={isLoaded ? '0px' : '10px'} mb={isLoaded ? '0px' : '30px'}>
            <Heading
              as="h4"
              lineHeight="1.5rem"
              fontSize="xl"
              fontWeight={700}
              display="inline"
              fontFamily="primary"
            >
              <HighlightFirstWord productLine={highlightedFirstWord ?? ''}/>
              {formatHeadlineColor(headline)}
            </Heading>
          </Skeleton>
          <SkeletonText
            isLoaded={isLoaded}
            noOfLines={5}
            skeletonHeight="1rem"
            mb={isLoaded ? '0px' : '30px'}
            spacing={2}
          >
            <Box fontSize="md" letterSpacing="0.45px" py={3}>{asComponent(text)}</Box>
          </SkeletonText>
          <Skeleton isLoaded={isLoaded} w={isLoaded ? 'auto' : '100px'} h={isLoaded ? 'auto' : '20px'}>
            <AnimatedUnderline
              display="inline-block"
              fontFamily="secondary"
              isPermaActive={Boolean(isMobile)}
            >
              <NextLink fontWeight="bold" fontSize="md" href={`/news${link.absolutePath}`}>
                {intl.formatMessage({ id: '--read-more' })}
              </NextLink>
            </AnimatedUnderline>
          </Skeleton>
        </Box>
      </Flex>
    </Stack>
  )
}
